// Central tunables. System-wide knobs only — per-entity specs keyed by id
// (SPECIES, BOSSES, MUTATION_DEFS, SKILL_DEFS) stay in their own data tables.
//
// Systems read T.* directly. Don't cache values across frames; the dev panel
// (src/game/dev/tweak-panel.ts) mutates this object in place at runtime.
//
// Excluded by design: buffer/stride sizes (shader-coupled, not knobs), DB
// keys, capacity caps that match shader bindings, internal computed maps.

const DEFAULTS = {
  player: {
    baseRadius: 0.12,
  },

  // Controls (amoeba-controls.md): WASD steers, cursor aims — the 2026-07
  // shootout winner. Losing schemes deleted 2026-07-10 (bead monorail-d3g).
  controls: {
    wasd: {
      accel: 7.0,        // approach rate toward target velocity
      stopDamping: 2.5,  // decel when no key held
      maxSpeed: 1.35,
    },
    pad: {
      innerDeadzone: 0.15, // below = stick noise, zeroed (scaled radial)
      outerDeadzone: 0.95, // above = full deflection (worn sticks never reach 1.0)
      zoomRate: 1.5,       // right-stick zoom speed, e-folds per second at full tilt
    },
  },

  // Aim assist (Phase C, amoeba-input plan): heading source only — mouse aim
  // stays raw with zero assist. One system serves keyboard-only now, pad
  // stick+soft-lock later.
  aim: {
    coneDeg: 80,        // full cone width for auto-target search
    maxRange: 2.5,      // world units — beyond this, no soft lock
    turnRateDeg: 720,   // max aim rotation deg/s — assist steers, never snaps
    stickyBonus: 0.25,  // score edge the current target keeps (no flicker)
    cursorHoldSec: 3,   // mouse silence before heading aim takes over
  },

  predator: {
    lungeRange: 0.65,
    windupDuration: 0.35,
    lungeDuration: 0.18,
    lungeSpeedMult: 3.0,
    recoveryDuration: 0.6,
    recoverySpeedMult: 0.4,
    windupCooldown: 1.5,
    wanderDriftAmp: 0.01,
    wanderDamping: 1.5,
    deepHpMult: 1.5,
    deepDamageMult: 1.4,
    deepRadiusMult: 1.15,
    // Deep windup 1.3× (not 1.4×): 0.49s tipped past comfortable reaction
    // time and read as "predator gave up halfway." Tuned 2026-05-02.
    deepWindupMult: 1.3,
    shallowTint: [1.0, 0.35, 0.15] as [number, number, number],
    // Visual direction 2026-07: threat is strictly the vermillion family.
    // Deep predators shift from purple to a darker blood-maroon — same
    // family as shallow, heavier register (was [0.55, 0.30, 0.55]).
    deepTint: [0.72, 0.24, 0.20] as [number, number, number],
    // Mushroom biome (tier 3) — a notch harder than deep, warm-earth threat
    // tint. Tier 3 is reachable now, so a tier-3 player belongs here untaxed;
    // the challenge is tougher predators + spores, NOT a crushing under-tier
    // tax that compounds with the predator mults into instant death. [PT feel]
    mushroomHpMult: 1.6,
    mushroomDamageMult: 1.5,
    mushroomRadiusMult: 1.2,
    mushroomWindupMult: 1.25,  // slower windup = more telegraph = fairer
    mushroomTint: [0.80, 0.32, 0.16] as [number, number, number],
    // Apex threat tint (monorail-228): a subset of predator species stay
    // dangerous at any player size (they never flee). They read as scarier via
    // a deep blood-crimson push — same vermillion family, darker/heavier
    // register than deep. Spawn blends the biome tint toward this so apexes are
    // legibly "the thing that will still hurt you." Kept saturated, not black —
    // the picture-book tone survives.
    // Vivid crimson-red (monorail-228, brightness pass): the old target went
    // muddy-brown under the vignette and read too close to normal fauna. Raised
    // luminance + blend so live apex tints land ~[0.81–0.90, 0.21–0.25, 0.15–0.17]
    // across biomes — clearly redder/brighter than a normal predator, still
    // saturated (not black). Paired with a self-lit FEAT2_APEX rim in the shader.
    apexTint: [0.85, 0.2, 0.15] as [number, number, number],
    apexTintBlend: 0.7,    // how far the biome tint is pulled toward apexTint
    baseCount: 4,
    maxCountAtDepth: 15,
    // Pressure comes from where you are and how long this life has lasted —
    // never from how big you've grown (growing is the reward, not the tax).
    depthCountScale: 1.5,   // target ×(1 + this×depthFactor)
    lifeRampSec: 90,        // +1 predator per this many seconds alive
    lifeRampMax: 4,         // cap on the per-life time ramp
    // Reversal: the player can contact-eat any predator whose radius is
    // under playerRadius / eatRatio. Predators that qualify flee on sight.
    eatRatio: 1.5,
    fleeSpeedMult: 1.15,
    targetRecruitable: 2,
    spawnRingMin: 7,
    spawnRingMax: 11,
    despawnDistance: 18,
    spawnMargin: 0.05,
    maxPlacementAttempts: 8,
  },

  ally: {
    orbitSpeed: 0.4,
    chargeSpeed: 0.85,
    returnSpeed: 0.45,
    orbitAngleSpeed: 0.5,
    predatorDetectRadius: 1.6,
    arrivalThreshold: 0.1,
    maxAllies: 3,
  },

  boss: {
    chain: {
      uncurlDuration: 1.2,
      idleSpeed: 0.12,
      strikeSpeed: 1.8,
      strikeDuration: 0.3,
      recoilDuration: 0.8,
      recoilSpeed: 0.5,
      coilAngularSpeed: 2.0,
      windupCooldownBase: 2.0,
      spawnRingMin: 10,
      spawnRingMax: 14,
      // monorail-228: attack + orbiter chip damage vs vulnerable segments is
      // scaled by this so dash (0.5/hit) stays the strongest weapon. Whip
      // (0.4 × 0.5 = 0.2) and orbiter (0.7 × 0.5 = 0.35) chip; dash reigns.
      weaponChipMult: 0.5,
    },
  },

  damage: {
    predatorMutationDropChance: 0.1,
    deepTier1Mult: 1.5,
    // Under-tier tax in the mushroom band. Light on purpose: a tier-3 player
    // belongs here untaxed, so this only bites players who arrive under-tiered;
    // a heavy tax would make the biome unplayable rather than a soft gate. [PT]
    mushroomTierMult: 1.3,
    hitFlashDuration: 0.15,
  },

  // Game-feel magnitudes (see amoeba-game-feel skill). Grouped per moment so
  // a whole beat can be swept together in the dev panel.
  feel: {
    evolve: {
      slowmoScale: 0.3,       // timeScale at the burst frame
      slowmoHoldSec: 0.12,
      slowmoRecoverSec: 0.4,
      burstShakeIntensity: 0.03,
      burstShakeDuration: 0.45,
      leanInZoomMult: 1.05,   // observer leans in during anticipation
      leanInZoomSec: 1.0,     // releases right at the burst frame
      settleZoomMult: 0.96,   // sits back after the burst — new body reads bigger
      settleZoomSec: 1.4,
      burstPushRange: 2.2,    // world units — microbes shoved by the pulse
      burstPushImpulse: 1.4,  // velocity impulse at epicenter, linear falloff
      // (eyes-closed duration is derived: morphDuration × burstEnd)
    },
    // Reversal bite — eating a thing that used to hunt you gets a small
    // hitstop dip so the power shift registers. Shallower than evolve.
    reversalEat: {
      slowmoScale: 0.55,
      slowmoHoldSec: 0.05,
      slowmoRecoverSec: 0.15,
    },
    // Floating damage/DNA numbers. Frequent beats coalesce:
    // DNA gains merge into one growing "+N", damage-dealt merges per target.
    numbers: {
      lifeSec: 0.9,          // label lifetime before full fade
      risePx: 26,            // screen-space drift upward over the lifetime
      gainCoalesceSec: 0.6,  // window in which a new DNA gain joins the live label
      dealtCoalesceSec: 0.45, // window in which repeat hits on one target merge
      // Damage sim units are small floats (dash 0.5, orbiter 0.7); scale for
      // display so numbers read as game integers ("5", "7"). Set 1 for raw.
      damageDisplayScale: 10,
    },
  },

  // Descent-toward-Heck flourish amounts per tier (amoeba-unification.md,
  // uniform contract in evolution.ts). Additive + cute, never darker. Zero
  // below each feature's entry tier; morph-blended between tiers.
  tierFlourish: {
    red2: 0.10, red3: 0.20, red4: 0.30, red5: 0.40, red6: 0.55,
    horns3: 0.4, horns4: 0.7, horns5: 0.8, horns6: 1.0,
    tail4: 0.5, tail5: 0.7, tail6: 1.0,
  },

  evolution: {
    // Tier costs live in TIER_SPECS (evolution.ts) — data table, not knobs.
    morphDuration: 2.0,
    tier2EdgeNoiseBonus: 0.25,
    anticipationEnd: 0.20,
    burstEnd: 0.50,
    anticipationDepth: 0.08,
    overshoot: 1.20,
  },

  growth: {
    sizeDeltaPerMicrobe: 0.015,
    maxSizeFromEating: 0.5,
    engulfSpeedMult: 0.7,
    sizeSkillMult: 1.3,
    // A reversal-eaten predator is worth this many microbes of growth.
    predatorGrowthUnits: 4,
    // Smallest a shrunk creature can get — edible even for a fresh player
    // (0.12 / eatRatio 1.5 = 0.08 threshold).
    shrinkFloor: 0.055,
  },

  // Divergent evolution (monorail-axe). Which critter you become is a weighted
  // argmax over a random per-form nudge (seedJitter) + normalized behavioral
  // signals. Whiskerkit carries a generalist baseline so a balanced run lands
  // there. Signals are normalized by refScale (a "typical amount" of each) so
  // seconds-of-dwell and event-counts compare fairly. Tune the whole shape live.
  lineage: {
    speciationTier: 3,     // the tier-up that locks the form
    revealDurationSec: 1.2, // ornament grow-in after the lock (burst → settle)
    seedJitter: 0.4,       // random 0..this added per form at run start
    whiskerkitBase: 0.8,   // generalist fallback weight
    stillSpeed: 0.25,      // speed under which "stillness" accrues
    // "typical amount" of each signal by the speciation moment (for normalizing)
    refScale: {
      predation: 6, grazing: 20, aggression: 8,
      stillness: 20, deepDwell: 30, mushDwell: 30, adaptations: 4,
    },
    // Per-form signal affinities (unit = normalized signal). Empty = baseline only.
    weights: {
      whiskerkit: {} as Record<string, number>,
      narwhalen: { predation: 1.0, aggression: 0.6 },
      anemonaut: { grazing: 0.8, stillness: 0.7 },
      frillgill: { mushDwell: 1.0, grazing: 0.4 },
      lanternet: { deepDwell: 1.0, predation: 0.3 },
    },
  },

  // Adaptations (amoeba-unification.md). Chunky: 3 fat levels each — every
  // card pick should feel like a gear change.
  powers: {
    osmoticReach: {
      reachFracPerLevel: 0.35, // eat-reach bonus per level, × body radius
    },
    endosymbiont: {
      orbitRadiusMult: 2.4,    // orbit distance, × body radius
      orbitSpeed: 1.8,         // radians/sec
      orbiterRadius: 0.045,
      damage: 0.7,
      hitCooldown: 0.7,        // per-orbiter seconds between predator hits
    },
    // Booster since A1 (monorail-g79): the attack verb itself lives in
    // weapons.ts def rows; Axopod multiplies whatever weapon is equipped.
    axopod: {
      damageMultPerLevel: 0.25, // L3: +75% attack damage
      reachMultPerLevel: 0.20,  // L3: +60% lash length / shot flight time
    },
    sporulation: {
      intervalBase: 2.4,       // seconds between volleys at L1
      intervalPerLevel: 0.75,  // shaved per level past L1
      range: 2.2,              // auto-target acquisition radius
      speed: 1.3,
      ttl: 1.8,
      sporeRadius: 0.035,
      shrinkBase: 0.014,       // radius removed per hit at L1
      shrinkPerLevel: 0.008,
    },
    galvanism: {
      intervalBase: 3.2,       // seconds between discharges at L1
      intervalPerLevel: 0.8,
      range: 1.6,              // first target acquisition
      chainRange: 1.1,         // per-jump reach; targets hit = level
      damage: 0.5,
    },
    // Former shop skills, folded into the pool (dash is innate, not here).
    flagellate: {
      speedAddPerLevel: 0.16,  // ×1.16 / 1.32 / 1.48
    },
    autophagy: {
      ratePerLevel: 0.45,      // HP/s per level
    },
    hypertrophy: {
      radiusAddPerLevel: 0.13, // ×1.13 / 1.26 / 1.39
      engulfCooldownShave: 2.0, // seconds off the engulf cooldown per level past L1
    },
    membraneLock: {
      durationBase: 1.6,
      durationPerLevel: 0.4,   // +0.4s per level past L1
      cooldownBase: 9.0,
      cooldownShave: 1.5,      // seconds off per level past L1
    },
  },

  // Growth-spurt cadence: first choice ~25 DNA in, ×1.6 each after
  // (25 → 40 → 64 → 102 → …). Early life drips choices, late life breathes.
  spurt: {
    firstThreshold: 25,
    thresholdGrowth: 1.6,
  },

  // Chunked with the unification: 3 stacks, fatter steps — a mutation card
  // should be felt on the next screen, not on a spreadsheet.
  mutations: {
    maxStacks: 3,
    eyesPerStack: 6,
    eyesTotalCap: 24,
    wetSocks: { speedMult: 0.85 },
    nucleusEnvy: { dnaMult: 1.4, maxHpMult: 0.85 },
    staticCling: { magnetRange: 1.1 },
    borrowedTeeth: { allyDamageMult: 1.45 },
    bouquet: { recruitReachPerStack: 0.07, detectionMult: 1.18 },
  },

  spawn: {
    microbeTarget: 25,
    microbeSpawnRingMin: 6,
    microbeSpawnRingMax: 9,
    microbeDespawnDistance: 14,
    // Share of food spawns rolled procedurally from the
    // biome palette (creature-procgen.ts) vs the curated named species.
    proceduralChance: 0.4,
  },

  // Overgrazing director (VS study): eating depletes the local cell's
  // microbe respawn odds; slow regen makes moving on the fed strategy.
  // ~8 microbes strips a cell; full recovery from bare takes ~2 minutes.
  graze: {
    cellSize: 3,
    perEat: 0.12,
    regenPerSec: 0.008,
  },

  biomes: {
    inner: 22,           // shallow→deep transition inner radius
    outer: 27,           // shallow→deep transition outer radius (boundary ~24.5)
    hubRadius: 4,        // depth axis (not a biome band)
    fullDepthDist: 22,   // depth axis
    mushroomInner: 42,   // deep→mushroom transition inner radius
    mushroomOuter: 47,   // deep→mushroom transition outer radius (boundary ~44.5)
  },

  // Innate/base active timings. Dash is innate (unification); shield and
  // engulf base values are modified per-level by T.powers.membraneLock /
  // T.powers.hypertrophy.
  skills: {
    dash: { speed: 4.0, duration: 0.20, cooldown: 1.5, iframes: 0.4 },
    engulf: { duration: 3.0, cooldown: 12.0, radiusBonus: 0.6 },
    // Town portal / recall (B5). The long cooldown IS the cost (research:
    // the release valve is paid for) — long enough that casting is a real
    // decision, short enough to stay retrieval QoL, not a dead button.
    portal: {
      cooldown: 120,
      gateRadius: 0.45,
      // Casting within this distance of home is a shrug, not a trip.
      minCastDist: 5,
    },
  },

  items: {
    // Relative weights (rollRarity normalizes). No common tier — nothing
    // droppable is trash.
    rarityWeights: { uncommon: 70, rare: 25, pristine: 5 },
    drop: {
      predatorBase: 0.06,       // kill-roll chance at the hub… (0.04 rolled
                                // zero drops in playtest round 1's session)
      predatorDepthBonus: 0.05, // …rising to ~11% at full depth
    },
  },

  // Breakable containers (B6). Health tuned against weapon damage 0.4–0.7:
  // two swipes with the starter, one with anything heavier. DNA is the
  // always-worth-a-swipe floor; the item roll stays BELOW predator odds so
  // furniture never out-drops fights.
  containers: {
    health: 0.8,
    radius: 0.13,
    roomChance: 0.65,       // chance a plain room gets furnished at all
    roomMax: 2,
    treasureCount: 3,       // treasure pockets keep paying a little after the orb
    rimFrac: [0.5, 0.8] as [number, number], // placement ring × room radius
    dnaMin: 2,
    dnaMax: 5,
    itemBase: 0.04,
    itemDepthBonus: 0.04,   // → ~8% at full depth
  },

  orbs: {
    dnaMagnetPullSpeed: 0.6,
    firstDeathOrbRadius: 0.3,
    mutationOrbRadius: 0.14,
    itemOrbRadius: 0.16,
    mutationMagnetPullSpeed: 0.6,
    mutationPickupReachGrace: 0.06,
  },

  npc: {
    approachExtra: 0.55,
    contactGrace: 0.04,
    reopenCooldown: 0.6,
  },

  // Mother Cell home comforts (bead iu2): heal aura + idle rest.
  mother: {
    auraRadius: 1.2,       // world units from her center
    auraRegen: 0.5,        // HP/s inside the aura (stacks with Autophagy)
    restIdleSec: 1.5,      // stillness before resting kicks in
    restRegenMult: 3.0,    // regen multiplier while resting
    restSpeedEps: 0.05,    // "still" means slower than this
    shopReach: 2.0,        // C opens the shop only within this range of her
  },

  // Daughter-cell checkpoints (bead monorail-s8k). Deliberately weaker than
  // the Mother (auraRadius 1.2 / auraRegen 0.5) — checkpoints cut travel,
  // they don't out-heal home.
  checkpoint: {
    auraRadius: 1.0,
    auraRegen: 0.3,
    attuneReachFrac: 0.55,
    cellRadius: 0.14,
  },

  eyes: {
    springK: 180,
    damping: 6,
    gravity: 1.2,
    restitution: 0.25,
    lookRefSpeed: 0.9,
    lookFraction: 0.65,
    angryRampRate: 4.0,
    angryRelaxRate: 1.0,
    blinkDuration: 0.18,
    blinkIntervalMin: 3.0,
    blinkIntervalMax: 7.0,
  },

  wander: {
    driftAmp: 0.02,
    damping: 2.0,
    burstSpeed: 0.10,
    burstIntervalMin: 1.5,
    burstIntervalRange: 2.5,
  },

  // Body-plan-coupled movement. Shape is
  // information: the darter reads evasive, the driller comes to you, the vault
  // sits still. All are edible food — flavor, not threat.
  creatures: {
    darter: {           // rod — fast erratic darts, flees when the player closes
      driftAmp: 0.03,
      damping: 1.4,
      burstSpeed: 0.55,
      burstIntervalMin: 0.4,
      burstIntervalRange: 0.7,
      fleeRadius: 0.85, // player within this range → dart away
      fleeSpeed: 0.85,
    },
    driller: {          // spiral — homes gently toward the player
      homeSpeed: 0.42,
      accel: 2.2,       // velocity approach rate toward homeSpeed
    },
    vault: {            // plant cell — sessile, high-value, slow to crack open
      radiusMin: 0.10,
      radiusMax: 0.13,
      damping: 6.0,     // strong damping → stays planted
      dna: 14,          // payout vs ~1–2 for a normal microbe
      absorbDuration: 0.6, // longer absorb = "cracking the wall"
    },
  },

  // Spore clouds + puffers (mushroom biome). Harder-but-whimsical: a
  // telegraphed cloud that slows + lightly chips, dodgeable, not a big hit.
  spore: {
    startRadius: 0.12,
    maxRadius: 0.85,
    life: 2.6,          // cloud lifetime (s)
    dps: 0.25,          // damage per 0.3s tick while inside
    slowLinger: 0.4,    // slow lasts this long after leaving the cloud
    slowMult: 0.7,      // movement speed while slowed (mobile enough to escape)
    pufferRange: 1.35,  // player proximity that triggers a puff
    pufferWindup: 0.7,  // telegraph before the puff
    pufferCooldown: 3.5,
    // Tier-3 identity beat: a specimen that belongs on the mushroom floor has
    // adapted to spores. At tier 3+ the DoT stops entirely and the slow is
    // gentler — you can wade the web, not just flee it. (Its own reward for
    // arriving properly evolved; under-tiered players still get impaired.)
    resilientTier: 3,
    resilientSlowMult: 0.88, // adapted slow (vs slowMult 0.7 for the untiered)
  },

  // Colony super-organisms. A
  // cluster of little cute-eyed cells that move as one and scatter into free
  // food the instant you bite a member — the piñata pop.
  colony: {
    maxColonies: 2,      // alive at once
    spawnChance: 0.10,   // per spawn tick when under the cap
    radius: 0.30,        // formation radius
    memberRadiusMin: 0.045,
    memberRadiusMax: 0.065,
    clumpCount: 5,
    ringCount: 6,
    lineCount: 5,
    spinRate: 0.5,       // ring/pair rotation (rad/s)
    followRate: 8.0,     // how fast members settle to their formation slot
    scatterSpeed: 0.9,   // clump: outward flee speed on disturbance
    // Per-layout v2 reactions.
    pairPanicSpeed: 1.4, // pair: the survivor bolts faster than a clump scatters
    ring: {
      coreRadius: 0.09,  // the central prize is a visibly bigger cell
      coreBounty: 12,    // DNA for cracking the core (vs ~1 for a ring cell)
      agitatedSpin: 2.4, // ring spins up when disturbed
      fleeSpeed: 0.75,   // the whole ring flees, carrying the core away
    },
    line: {
      whipSpeed: 1.6,    // members are flung sideways when the line is struck
    },
    // Size-gated whole-swallow: a big enough (or engulfing) player gulps the
    // whole colony at once — the growth payoff.
    wholeSwallowRatio: 4.0, // player radius ≥ member radius × this
    // Mycelial network (mushroom floor): a drifting raft of mushroom nodes
    // joined by hyphae threads. Biting a node ripples spore puffs down the web.
    network: {
      maxNetworks: 1,     // alive at once (a set-piece)
      spawnChance: 0.05,
      nodeCount: 6,
      nodeRadius: 0.09,
      hopDelay: 0.35,     // seconds per graph-hop for the ripple wavefront
      bobAmp: 0.05,       // gentle per-node sway
    },
  },

  regen: {
    rate: 0.5,
  },

  audio: {
    music: {
      level: 0.16,      // per-voice ceiling for zone tracks
      dangerDuck: 0.35, // fraction the whole music layer ducks under threat
    },
    ambient: {
      // Lowpassed-noise low end — felt more than heard. Not comparable to the
      // 0.05 the two-sine drone used before it: a lowpass at ~110 Hz passes
      // roughly 0.5% of white noise's power, so matching the old bed's RMS
      // would want ≈1.2 here. 0.35 is therefore about 11 dB *below* where the
      // drone sat, on purpose — its level was part of the complaint. This is
      // the knob to raise (dev panel, Audio ▸ ambient) if the deep zone feels
      // thin; the arithmetic above says the ceiling before it's loud is ~1.2.
      rumbleLevel: 0.35,
      washLevel: 0.035,        // filtered-noise water texture
      rushLevel: 0.22,         // near-current noise swell (before ×prox²)
      heartbeatLevel: 0.5,
      heartbeatThreshold: 0.34, // hpFrac at/below which the heart is audible
    },
    // Tension scalar shaping (systems/ambient.ts)
    tensionRadius: 3.0,        // predator within this range starts raising tension
    tensionFloorWindup: 0.9,   // min tension while any predator winds up / lunges
    tensionFloorBossAwake: 0.7,
    rushRadius: 1.6,           // extra reach beyond a current hazard's radius
  },

  narrator: {
    eventCooldownMs: 8_000,
    ambientCooldownMs: 90_000,
    ambientIdleMinSec: 15,
    captionDurationSec: 3.5,
    queueGapMs: 400,
    bathroomSilenceMinMs: 25_000,
    bathroomSilenceMaxMs: 35_000,
  },

  // Dungeon generator (B1, monorail-fgy). Geometry knobs shared by the
  // embedder, carver, CPU collision, and walls.slang. Player radius is 0.12
  // at tier 1, so a 0.55 hall half-width is a wide corridor for a fresh
  // specimen and a squeeze for a grown one.
  dungeon: {
    roomBaseR: 1.6,       // main room-disc radius at sizeHint 1
    hallHalfWidth: 0.55,  // capsule radius for plain halls
    // Floor for passable halls: a maxed specimen (tier 6 + hypertrophy +
    // full eating growth ≈ 0.65 radius) must fit everywhere ungated.
    minHallHalfWidth: 0.72,
    hallLength: 2.6,      // open hall between room rims (embed rest length)
    sminK: 0.45,          // smooth-union rounding (world units)
    embedIterations: 250, // force-relax steps (deterministic, cooled)
    repelPad: 1.4,        // extra clearance between non-adjacent rooms
    doorReach: 0.3,       // contact margin for opening a shortcut door
  },
}

// ── HMR hot boundary ────────────────────────────────────────────────────────
// Editing a value above patches the running game without a reload, so feel
// tuning doesn't cost you your mid-run state. The live T object survives the
// swap via hot.data (importers keep their reference); only keys whose source
// literal actually changed are re-assigned, so Tweakpane tweaks made this
// session on OTHER knobs are preserved.

export const T: typeof DEFAULTS = import.meta.hot?.data.liveT ?? DEFAULTS

type Tree = Record<string, unknown>

/** Assign leaves of `next` onto `live` wherever `next` differs from `prev`.
 *  Nested object identities in `live` are preserved (panel bindings stay valid). */
function assignChanged(live: Tree, prev: Tree, next: Tree): void {
  for (const key of Object.keys(next)) {
    const n = next[key]
    const p = prev[key]
    if (n && typeof n === 'object' && !Array.isArray(n)) {
      if (live[key] && typeof live[key] === 'object') {
        assignChanged(live[key] as Tree, (p ?? {}) as Tree, n as Tree)
      } else {
        live[key] = n
      }
    } else if (JSON.stringify(n) !== JSON.stringify(p)) {
      live[key] = n
    }
  }
}

if (import.meta.hot) {
  const prevDefaults = import.meta.hot.data.defaults as typeof DEFAULTS | undefined
  import.meta.hot.data.liveT = T
  import.meta.hot.data.defaults = DEFAULTS
  if (prevDefaults) {
    assignChanged(T as Tree, prevDefaults as Tree, DEFAULTS as Tree)
  }
  import.meta.hot.accept()
}
